import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import UomDesign from "@/components/UomDesign";
import { EditMasterDialog } from "@/components/masters/EditMasterDialog";

export default function UnitsOfMeasure() {
  const { can } = usePermissions();
  const canCreateUom = can("UNITS", "create");
  const canEditUom = can("UNITS", "edit");
  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/uom");
      setUoms(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (uom) => {
    try {
      await api.patch(`/masters/uom/${uom.id}`, { is_active: !uom.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const shown = useMemo(() => {
    if (!search) return uoms;
    const q = search.toLowerCase();
    return uoms.filter((u) =>
      (u.code || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q)
    );
  }, [uoms, search]);

  return (
    <div className="space-y-6" data-testid="uom-page">
      <UomDesign uoms={uoms} shown={shown} loading={loading} search={search} onSearch={setSearch} canManage={canEditUom} canDelete={can("UNITS", "delete")}
        onToggle={toggleActive} onView={setSelected} onChanged={load}
        createAction={canCreateUom && <CreateUomDialog onCreated={load} />} />

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={`UOM · ${selected?.code}`}
      >
        <DrawerSection label="Unit Details">
          <DrawerField label="Code" value={selected?.code} mono />
          <DrawerField label="Name" value={selected?.name} />
          <DrawerField label="Status" value={selected?.is_active ? "Active" : "Inactive"} />
        </DrawerSection>
      </DetailDrawer>
    </div>
  );
}

function CreateUomDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/masters/uom", form);
      toast.success(`UoM ${form.code} created`);
      setForm({ code: "", name: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-uom-button" data-tour="uom-add" className="h-11 gap-2 rounded-lg bg-[#910029] px-5 text-sm font-semibold text-white shadow-sm hover:bg-[#7A0022]"><Plus className="h-4 w-4" /> New UoM</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Unit of Measure</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-uom-form">
          <div>
            <Label>Code</Label>
            <Input placeholder="Automatic if blank" data-testid="uom-code-input" value={form.code} onChange={set("code")} className="mt-1.5" />
            <FieldError error={errors.code} />
          </div>
          <div>
            <Label>Name</Label>
            <Input required data-testid="uom-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="Numbers" />
            <FieldError error={errors.name} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-uom-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
